import { useEffect } from "react";
import { useMusicContext } from "@/providers/MusicProvider";
import type { MusicKey } from "@/lib/audio/musicMap";

/**
 * Declaratively bind a scene's music. Crossfades when `key` changes and is a
 * no-op when the same key is passed across renders.
 *
 * IMPORTANT: cleanup does NOT call `stop()`. The next scene's `play()` handles
 * the crossfade, and `MusicProvider` already stops music when the route leaves
 * the music-scope. Calling `stop()` in cleanup raced the next effect's
 * `play()` and caused overlapping background tracks during Knowledge Checks
 * (the cleanup nulled `currentKey`, then the immediate re-mount's `play()`
 * restarted the element from `currentTime=0` while the fade-out timer from
 * the cleanup was still resolving — audible double-track under React Strict
 * Mode and on rapid key changes).
 */
export function useSceneMusic(
  key: MusicKey | null,
  opts?: { volume?: number; active?: boolean }
) {
  const { setSceneMusic } = useMusicContext();
  const active = opts?.active ?? true;
  const volume = opts?.volume;

  useEffect(() => {
    if (!active || !key) return;
    setSceneMusic(key, volume != null ? { volume } : undefined);
    // No cleanup stop — see note above.
  }, [key, active, volume, setSceneMusic]);
}